/**
 * Feature Flags Service
 *
 * Build-time feature flags resolved from Vite environment variables.
 * Frontend flags use VITE_FF_* prefix.
 *
 * Usage:
 *   import { isFeatureEnabled, useFeatureFlag } from "./services/feature-flags";
 *
 *   if (isFeatureEnabled("EVIDENCE_UPLOAD")) { ... }
 *   const enabled = useFeatureFlag("CORRECTED_TEXT_OUTPUT");
 */

/**
 * Feature flag definitions
 * Values are resolved at build time from Vite env vars
 */
const metaEnv =
  (import.meta as unknown as { env?: Record<string, string | undefined> })
    .env ?? {};

// Hiding recommendations also disables rewrite views and their deep links.
// The deploy script enables this for the live app, not staging.
const HIDE_RECOMMENDATIONS = metaEnv.VITE_FF_HIDE_RECOMMENDATIONS === "true";

const FLAGS = {
  // Reports UI (enabled by default)
  REPORTS: metaEnv.VITE_FF_REPORTS !== "false",

  // Phase 2: Evidence features
  EVIDENCE_UPLOAD: metaEnv.VITE_FF_EVIDENCE_UPLOAD !== "false",
  FILE_UPLOAD: metaEnv.VITE_FF_FILE_UPLOAD !== "false",

  HIDE_RECOMMENDATIONS,

  // Phase 3 (Messaging retired, per internal feedback: COMPLIANT_MESSAGING removed
  // 2026-08-08 — the ClaimMessaging panel it gated was deleted)
  CORRECTED_TEXT_OUTPUT: !HIDE_RECOMMENDATIONS &&
    metaEnv.VITE_FF_CORRECTED_TEXT_OUTPUT !== "false",

  // Per-Use Billing: Billing mode flags. PREPAID/METERED enabled by default;
  // SUBSCRIPTIONS and ADMIN_TOP_UP_ONLY stay off (carve-outs, 2026-08-04
  // cleanup sweep — see backend/src/config/feature-flags.ts for why).
  BILLING_SUBSCRIPTIONS: metaEnv.VITE_FF_BILLING_SUBSCRIPTIONS === "true",
  BILLING_PREPAID: metaEnv.VITE_FF_BILLING_PREPAID !== "false",
  BILLING_METERED: metaEnv.VITE_FF_BILLING_METERED !== "false",
  BILLING_ADMIN_TOP_UP_ONLY:
    metaEnv.VITE_FF_BILLING_ADMIN_TOP_UP_ONLY === "true",

  // Phase 2: Knowledge Base (enabled by default)
  KNOWLEDGE_BASE: metaEnv.VITE_FF_KNOWLEDGE_BASE !== "false",

  // Inline Claims Review: diff-style view with accept/download. Default
  // OFF (pirx-v39e3, 2026-08-06) -- hidden pending UX polish; flip with
  // VITE_FF_INLINE_REVIEW=true. Forced OFF while HIDE_RECOMMENDATIONS is on.
  INLINE_REVIEW: !HIDE_RECOMMENDATIONS &&
    metaEnv.VITE_FF_INLINE_REVIEW === "true",

  // Hide Classification Badges: hide CZARNA/SZARA badges in report views (enabled by default)
  HIDE_CLASSIFICATION_BADGES:
    metaEnv.VITE_FF_HIDE_CLASSIFICATION_BADGES !== "false",

  // Report Legal Basis: render legal-basis ("podstawa prawna") rows + section 4
  // (enabled by default; mirrors backend FF_REPORT_LEGAL_BASIS for defense in depth)
  REPORT_LEGAL_BASIS: metaEnv.VITE_FF_REPORT_LEGAL_BASIS !== "false",

  // Website Monitoring: recurring re-audit of a direct URL source (enabled by
  // default; mirrors backend WEBSITE_MONITORING flag). See pirx-8mm1n.
  WEBSITE_MONITORING: metaEnv.VITE_FF_WEBSITE_MONITORING !== "false",

  // PostHog Support / Conversations (pirx-p953i): mirrors backend
  // FF_POSTHOG_CONVERSATIONS. Kept off (carve-out) — see
  // docs/observability/overview.md's 3-part prerequisite gate (unmet
  // inbound channel + SAR/erasure smoke test) and
  // docs/policies/privacy-policy.md's "Conditional" disclosure.
  POSTHOG_CONVERSATIONS: metaEnv.VITE_FF_POSTHOG_CONVERSATIONS === "true",

  // Test Item (pirx-8ydla): expose the gated €1 / 1-credit test item in the
  // billing view (default ON; mirrors backend BILLING_TEST_ITEM). On dev this
  // lets a real-card purchase validate the live Stripe purchase→credit flow.
  TEST_ITEM_STRIPE: metaEnv.VITE_FF_TEST_ITEM_STRIPE !== "false",

  // Teams (pirx-qy6v4): team workspaces — switcher, settings members/invite,
  // invite-accept route. Default OFF: the teams feature is not ready to roll
  // out. All team-related UI stays hidden until this is set to "true".
  TEAMS: metaEnv.VITE_FF_TEAMS === "true",

  // Custom rewrite styles ("Style własnego brzmienia" settings tab; spec
  // docs/plans/2026-08-02-custom-rewrite-styles-spec.md). Mirrors backend
  // CUSTOM_REWRITE_STYLES (default off). Default OFF: the settings tab is
  // hidden until this is set to "true".
  CUSTOM_REWRITE_STYLES: metaEnv.VITE_FF_CUSTOM_REWRITE_STYLES === "true",

  // Hide Reformulation View ("ukryj widok proponowanej reformulacji", PRD
  // docs/prd/2026-10-27-meeting-changes.md item 3). Hides every surface that
  // shows a rewritten claim — the safer-phrasing panel, the rewrite status
  // notices, the "Improved version" tab, and (mirrored by the backend flag of
  // the same name) the rewrite panels in the claims PDF. The recommendation
  // text stays visible: the tester's complaint is about the rewrite, not
  // about the advice. Default OFF so existing tests and a local build show
  // today's behaviour; deploy-frontend.ts pins it ON. See
  // docs/changelog/2026-10-27_tester-corpus-followup.md.
  HIDE_REFORMULATION_VIEW: metaEnv.VITE_FF_HIDE_REFORMULATION_VIEW === "true",
} as const;

export type FlagName = keyof typeof FLAGS;

/**
 * Check if a feature flag is enabled
 */
export function isFeatureEnabled(flag: FlagName): boolean {
  return FLAGS[flag];
}

/**
 * React hook for feature flags
 * Note: Values are static (build-time), no re-renders needed
 */
export function useFeatureFlag(flag: FlagName): boolean {
  return FLAGS[flag];
}

/**
 * Get all flags (useful for debug panel)
 */
export function getAllFlags(): Record<FlagName, boolean> {
  return { ...FLAGS };
}

/**
 * Check multiple flags at once
 */
export function areFeaturesEnabled(...flags: FlagName[]): boolean {
  return flags.every((flag) => FLAGS[flag]);
}
